import {
  Text,
  VStack,
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Badge,
  HStack,
  Divider,
  Flex,
  SimpleGrid,
} from "@chakra-ui/react";
import { ConciliacaoStatus } from "@/models/consignado/propostaFinanceira.model";

export interface ConciliationAvaliacao {
  regraId: string;
  operador: string;
  descricao: string;
  resultado: boolean;
  valorOrigem: number | null;
  valorDestino: number | null;
  rotulo_origem?: string,
  rotulo_destino?: string
}

export interface ConciliationData {
  title: string;
  status: string;
  observacao: string;
  avaliacoes: ConciliationAvaliacao[];
  source1Name?: string; 
  source2Name?: string;
}

interface ConciliationDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  conciliationData: ConciliationData | null;
}

export default function ConciliationDetailsModal({
  isOpen,
  onClose,
  conciliationData,
}: ConciliationDetailsModalProps) {
  const badgeColorScheme = conciliationData?.status === ConciliacaoStatus.DIVERGENTE
    ? "red"
    : conciliationData?.status === ConciliacaoStatus.NAO_SE_APLICA
      ? "gray"
      : conciliationData?.status === ConciliacaoStatus.PENDENTE
        ? "yellow"
        : "green";
  
  // const formatCurrency = (value: number | null) => {
  //   if (value === null || value === undefined) return "Não disponível";
  //   return new Intl.NumberFormat("pt-BR", {
  //     style: "currency",
  //     currency: "BRL",
  //   }).format(value);
  // };

  // 1. Calcula a quantidade de regras
  const regrasCount = conciliationData?.avaliacoes?.length || 0;
  
  // 2. Define o número de colunas (máximo 3, dividindo por blocos de 4 regras)
  const numCols = Math.min(3, Math.ceil(regrasCount / 4));

  // 3. Ajusta o tamanho do Modal para comportar as colunas sem espremer
  const modalSize = numCols === 1 ? "2xl" : numCols === 2 ? "4xl" : "6xl";

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size={modalSize} 
      isCentered 
      scrollBehavior="inside" // Importante: garante a rolagem apenas na lista, não na tela toda
    >
      <ModalOverlay />
      <ModalContent maxH="85vh">
        <ModalHeader borderBottomWidth="1px" pb={4}>
          Detalhes da Conciliação
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={6}>
          {conciliationData && (
            <VStack spacing={5} align="stretch">
              
              {/* Cabeçalho */}
              <Box>
                <HStack justifyContent="space-between" mb={2}>
                  <Text fontSize="sm" color="gray.600" fontWeight="semibold">
                    Etapa de Conciliação:
                  </Text>
                  <Badge 
                    colorScheme={badgeColorScheme}
                    px={2} py={1} borderRadius="md"
                  >
                    {conciliationData.status}
                  </Badge>
                </HStack>
                <Text fontSize="lg" fontWeight="bold">
                  {conciliationData.title}
                </Text>
                <Text fontSize="sm" color="gray.500" mt={1}>
                  {conciliationData.observacao}
                </Text>
              </Box>

              <Divider />

              {/* Grid Inteligente */}
              <Box px={1}>
                <SimpleGrid
                  columns={{ base: 1, lg: numCols }} // Responsividade: 1 col no celular, multi-col no PC
                  spacing={6}
                  mx={numCols === 1 ? "auto" : "0"} // Centraliza se for apenas 1 coluna
                  maxW={numCols === 1 ? "700px" : "100%"} // Limita a largura se for 1 coluna para não esticar
                >
                  {conciliationData.avaliacoes.map((avaliacao, index) => {
                    const diff = (avaliacao.valorOrigem || 0) - (avaliacao.valorDestino || 0);
                    const isSuccess = avaliacao.resultado;

                    return (
                      <Box 
                        key={index} 
                        p={3} 
                        borderWidth="1px" 
                        borderRadius="md" 
                        borderColor={isSuccess ? "green.200" : "red.200"} 
                        bg={isSuccess ? "green.50" : "red.50"}
                        borderLeftWidth="4px"
                        borderLeftColor={isSuccess ? "green.500" : "red.500"}
                        transition="all 0.2s"
                        _hover={{ shadow: "sm" }}
                      >
                        <Flex justifyContent="space-between" alignItems="center" mb={2}>
                          <Text fontSize="sm" fontWeight="bold" color="gray.700" noOfLines={1} pr={2}>
                            {avaliacao.descricao || `Regra ${index + 1}`}
                          </Text>
                          <Badge variant="subtle" colorScheme={isSuccess ? "green" : "red"} fontSize="2xs">
                            {isSuccess ? "CONFERE" : "DIVERGENTE"}
                          </Badge>
                        </Flex>

                        <Flex 
                          justifyContent="space-between" 
                          alignItems="center" 
                          bg="white" 
                          p={2} 
                          borderRadius="sm" 
                          borderWidth="1px" 
                          borderColor="gray.100"
                        >
                          <VStack align="start" spacing={0} flex={1}>
                            <Text fontSize="2xs" color="gray.500" fontWeight="bold" textTransform="uppercase">
                              {avaliacao.rotulo_origem || conciliationData.source1Name || "Origem"}
                            </Text>
                            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                              {avaliacao.valorOrigem}
                            </Text>
                          </VStack>

                          <Text fontSize="xs" color="gray.300" fontWeight="bold" px={2}>
                            VS
                          </Text>

                          <VStack align="start" spacing={0} flex={1}>
                            <Text fontSize="2xs" color="gray.500" fontWeight="bold" textTransform="uppercase">
                              {avaliacao.rotulo_destino || conciliationData.source2Name || "Destino"}
                            </Text>
                            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                              {avaliacao.valorDestino}
                            </Text>
                          </VStack>

                          {(!isSuccess || avaliacao.valorOrigem !== avaliacao.valorDestino) && (
                            <VStack align="end" spacing={0} flex={1} borderLeftWidth="1px" borderColor="red.100" pl={2} ml={2}>
                              <Text fontSize="2xs" color="red.400" fontWeight="bold" textTransform="uppercase">
                                Diferença
                              </Text>
                              <Text fontSize="sm" fontWeight="bold" color="red.600">
                                {diff.toFixed(2)}
                              </Text>
                            </VStack>
                          )}
                        </Flex>
                      </Box>
                    );
                  })}
                </SimpleGrid>
              </Box>

            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}